/*
 * IconTagInput — iter-215 P3
 * ---------------------------
 * Drop-in replacement for <TagInput> that lets vendors pick a Lucide
 * icon for each inclusion/exclusion tag. Values are stored as either:
 *   - string                     (legacy, no icon)
 *   - { icon?: string, label }   (new, with icon key)
 *
 * The backend & BuyGuardPreview already accept both shapes.
 *
 * Design choices per user direction:
 *   • Lucide-first — only catalogued icons we already have in the bundle.
 *   • No Iconify unless a vendor flags a missing icon (future story).
 *   • Icons organised by vertical (food / electronics / service / etc.)
 *     with a single search box to flatten them all.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  // Universal
  CheckCircle2, XCircle, Shield, Award, Star, Tag as TagIcon,
  Clock, Calendar, MapPin, Gift, Sparkles, Info,
  // Shipping / logistics
  Truck, Package, Box, PackageCheck, PackageX, RefreshCw,
  // Electronics / tech
  Smartphone, Headphones, Wifi, Battery, Plug, Usb,
  // Food / grocery
  Utensils, Coffee, Cookie, Apple, Pizza, Wheat, Beef,
  // Service / manual work
  Wrench, Hammer, Brush, Scissors, Settings, Zap,
  // Books / education
  BookOpen, GraduationCap, FileText, PenTool,
  // Health / wellness
  Heart, Activity, Dumbbell, Pill, Stethoscope,
  // Home / interior
  Home, Sofa, BedDouble, Lamp,
  // Warnings / negative
  AlertTriangle, Ban, X, Minus,
  // Misc control
  Search, Plus, ChevronDown,
} from 'lucide-react';
import { Input } from './ui/input';

/** Curated icon registry. Order matters for visual grouping. */
const ICON_CATALOG = [
  // — Universal (default / safe choices)
  { key: 'check', Icon: CheckCircle2, group: 'universal', terms: 'included yes ok' },
  { key: 'cross', Icon: XCircle, group: 'universal', terms: 'excluded no not' },
  { key: 'shield', Icon: Shield, group: 'universal', terms: 'warranty protection safe' },
  { key: 'award', Icon: Award, group: 'universal', terms: 'certified genuine quality' },
  { key: 'star', Icon: Star, group: 'universal', terms: 'premium featured highlighted' },
  { key: 'tag', Icon: TagIcon, group: 'universal', terms: 'label category' },
  { key: 'clock', Icon: Clock, group: 'universal', terms: 'time duration hours' },
  { key: 'calendar', Icon: Calendar, group: 'universal', terms: 'date schedule' },
  { key: 'map-pin', Icon: MapPin, group: 'universal', terms: 'location area' },
  { key: 'gift', Icon: Gift, group: 'universal', terms: 'bonus complimentary free' },
  { key: 'sparkles', Icon: Sparkles, group: 'universal', terms: 'new special magic' },
  { key: 'info', Icon: Info, group: 'universal', terms: 'information note' },

  // — Shipping / logistics
  { key: 'truck', Icon: Truck, group: 'shipping', terms: 'delivery shipping freight' },
  { key: 'package', Icon: Package, group: 'shipping', terms: 'box parcel courier' },
  { key: 'box', Icon: Box, group: 'shipping', terms: 'packaging carton' },
  { key: 'package-check', Icon: PackageCheck, group: 'shipping', terms: 'delivered received' },
  { key: 'package-x', Icon: PackageX, group: 'shipping', terms: 'undelivered returned' },
  { key: 'refresh', Icon: RefreshCw, group: 'shipping', terms: 'return exchange refund' },

  // — Electronics / tech
  { key: 'smartphone', Icon: Smartphone, group: 'electronics', terms: 'phone mobile device' },
  { key: 'headphones', Icon: Headphones, group: 'electronics', terms: 'audio earbuds speaker' },
  { key: 'wifi', Icon: Wifi, group: 'electronics', terms: 'wireless bluetooth internet' },
  { key: 'battery', Icon: Battery, group: 'electronics', terms: 'power charge cell' },
  { key: 'plug', Icon: Plug, group: 'electronics', terms: 'charger adapter socket' },
  { key: 'usb', Icon: Usb, group: 'electronics', terms: 'cable port data' },

  // — Food / grocery
  { key: 'utensils', Icon: Utensils, group: 'food', terms: 'meal restaurant tiffin' },
  { key: 'coffee', Icon: Coffee, group: 'food', terms: 'tea beverage drink' },
  { key: 'cookie', Icon: Cookie, group: 'food', terms: 'snack bakery sweet' },
  { key: 'apple', Icon: Apple, group: 'food', terms: 'fruit healthy fresh' },
  { key: 'pizza', Icon: Pizza, group: 'food', terms: 'meal italian fast' },
  { key: 'wheat', Icon: Wheat, group: 'food', terms: 'grain bread gluten' },
  { key: 'beef', Icon: Beef, group: 'food', terms: 'meat non-veg protein' },

  // — Service / manual work
  { key: 'wrench', Icon: Wrench, group: 'service', terms: 'repair fix tool' },
  { key: 'hammer', Icon: Hammer, group: 'service', terms: 'build construction' },
  { key: 'brush', Icon: Brush, group: 'service', terms: 'paint clean cosmetic' },
  { key: 'scissors', Icon: Scissors, group: 'service', terms: 'haircut salon tailor' },
  { key: 'settings', Icon: Settings, group: 'service', terms: 'configure tune-up' },
  { key: 'zap', Icon: Zap, group: 'service', terms: 'electric fast quick' },

  // — Education
  { key: 'book-open', Icon: BookOpen, group: 'education', terms: 'book study textbook' },
  { key: 'graduation-cap', Icon: GraduationCap, group: 'education', terms: 'degree certificate' },
  { key: 'file-text', Icon: FileText, group: 'education', terms: 'document notes' },
  { key: 'pen-tool', Icon: PenTool, group: 'education', terms: 'writing draw' },

  // — Health / wellness
  { key: 'heart', Icon: Heart, group: 'health', terms: 'love wellness care' },
  { key: 'activity', Icon: Activity, group: 'health', terms: 'pulse heartbeat monitor' },
  { key: 'dumbbell', Icon: Dumbbell, group: 'health', terms: 'gym workout fitness' },
  { key: 'pill', Icon: Pill, group: 'health', terms: 'medicine pharma tablet' },
  { key: 'stethoscope', Icon: Stethoscope, group: 'health', terms: 'doctor clinic consult' },

  // — Home / interior
  { key: 'home', Icon: Home, group: 'home', terms: 'house residence' },
  { key: 'sofa', Icon: Sofa, group: 'home', terms: 'furniture couch' },
  { key: 'bed-double', Icon: BedDouble, group: 'home', terms: 'bedroom stay' },
  { key: 'lamp', Icon: Lamp, group: 'home', terms: 'light lighting' },

  // — Negative / warning
  { key: 'alert-triangle', Icon: AlertTriangle, group: 'warning', terms: 'warning caution alert' },
  { key: 'ban', Icon: Ban, group: 'warning', terms: 'forbidden not-allowed' },
  { key: 'x', Icon: X, group: 'warning', terms: 'remove close cancel' },
  { key: 'minus', Icon: Minus, group: 'warning', terms: 'less subtract' },
];

const ICON_MAP = Object.fromEntries(ICON_CATALOG.map(x => [x.key, x.Icon]));

/** Render a Lucide icon by key. Returns null for unknown keys. */
export const IconByKey = ({ iconKey, className = 'h-4 w-4' }) => {
  if (!iconKey) return null;
  const Ico = ICON_MAP[iconKey];
  return Ico ? <Ico className={className} /> : null;
};

/**
 * IconTagInput
 * @param {Array<string|{icon?:string,label:string}>} tags
 * @param {(tags)=>void} onChange
 */
export const IconTagInput = ({
  tags = [],
  onChange,
  placeholder = 'Type and press Enter...',
  testId,
  className = '',
}) => {
  const [draft, setDraft] = useState('');
  const [pickerOpen, setPickerOpen] = useState(null); // index of tag currently picking an icon
  const [pickerSearch, setPickerSearch] = useState('');
  const popRef = useRef(null);

  // Normalise any incoming tag to { icon, label }.
  const normalised = useMemo(
    () => (tags || []).map(t =>
      typeof t === 'string' ? { icon: null, label: t } : { icon: t?.icon || null, label: t?.label || '' }
    ),
    [tags]
  );

  const emit = (next) => {
    // Keep strings as strings for tags without an icon — preserves the
    // backend string[] shape for legacy listings.
    const out = next.map(t => (t.icon ? { icon: t.icon, label: t.label } : t.label));
    onChange?.(out);
  };

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    emit([...normalised, { icon: null, label: v }]);
    setDraft('');
  };

  const remove = (i) => emit(normalised.filter((_, j) => j !== i));

  const setIconAt = (i, iconKey) => {
    const next = [...normalised];
    next[i] = { ...next[i], icon: iconKey };
    emit(next);
    setPickerOpen(null);
    setPickerSearch('');
  };

  const filteredCatalog = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return ICON_CATALOG;
    return ICON_CATALOG.filter(i =>
      i.key.includes(q) || i.terms.includes(q) || i.group.includes(q)
    );
  }, [pickerSearch]);

  // Close picker on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (pickerOpen != null && popRef.current && !popRef.current.contains(e.target)) {
        setPickerOpen(null); setPickerSearch('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  return (
    <div className={`space-y-2 ${className}`} data-testid={testId}>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 text-sm rounded-md bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
          disabled={!draft.trim()}
          data-testid={testId ? `${testId}-add` : undefined}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {normalised.map((tag, i) => (
          <div key={i} className="relative">
            <div
              className="inline-flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 rounded-full pl-2 pr-1 py-1 text-sm"
              data-testid={testId ? `${testId}-tag-${i}` : undefined}
            >
              <button
                type="button"
                onClick={() => { setPickerOpen(i); setPickerSearch(''); }}
                className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-white hover:bg-indigo-100 border border-gray-300"
                title="Pick icon"
                data-testid={testId ? `${testId}-pick-${i}` : undefined}
              >
                {tag.icon
                  ? <IconByKey iconKey={tag.icon} className="h-3.5 w-3.5 text-indigo-600" />
                  : <ChevronDown className="h-3 w-3 text-gray-500" />}
              </button>
              <span className="px-1">{tag.label}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-red-100 text-red-600"
                title="Remove"
                data-testid={testId ? `${testId}-remove-${i}` : undefined}
              >
                <X className="h-3 w-3" />
              </button>
            </div>

            {pickerOpen === i && (
              <div
                ref={popRef}
                className="absolute top-full left-0 mt-1 z-50 w-72 max-h-80 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg p-3"
                data-testid={testId ? `${testId}-picker-${i}` : undefined}
              >
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    autoFocus
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    placeholder="Search icons..."
                    className="pl-7 h-8 text-xs"
                  />
                </div>
                <div className="mb-2">
                  <button
                    type="button"
                    onClick={() => setIconAt(i, null)}
                    className="text-xs text-gray-500 hover:underline"
                  >
                    Remove icon
                  </button>
                </div>
                <div className="grid grid-cols-6 gap-1.5">
                  {filteredCatalog.map(({ key, Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setIconAt(i, key)}
                      title={key}
                      className={`h-9 w-9 rounded-md flex items-center justify-center border hover:bg-indigo-50 hover:border-indigo-400 ${
                        tag.icon === key ? 'bg-indigo-100 border-indigo-500' : 'border-gray-200'
                      }`}
                      data-testid={`icon-opt-${key}`}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  ))}
                  {filteredCatalog.length === 0 && (
                    <p className="col-span-6 text-xs text-gray-400 italic py-2">
                      No icons match "{pickerSearch}". Tell the Aniyor team and we'll add it.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IconTagInput;
